import React, { useCallback, useMemo } from "react";
import styles from "./TabPanel.module.css";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { Trans } from "@coworker/locales";
import { LoadMore } from "../../../shared/components/LoadMore/LoadMore";
import { useItemsInfoQueries } from "../../../shared/hooks/item/useItemsInfo";
import { Item } from "../../../shared/types/item";
import { FullTask } from "../../types/FullTask";
import { useTeamId, useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import StackIconPath from "../../../shared/wrappers/FixaSSRIcon/paths/StackIconPath";
import { ModalContext } from "../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../layout/context/modalType";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import {
  TaskListItem,
  useTasksOverview,
} from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { SwipeableTaskCard } from "@coworker/apprestructured/src/tasks/components/TaskCard/SwipeableTaskCard";
import { shouldDisplayPickupTask } from "@coworker/app/src/components/TaskDetail/actions/helpers";
import TaskCard from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import ListableTaskCardWrapper from "@coworker/apprestructured/src/task-management/components/TaskCard/wrappers/NormalTaskWrapper/ListableTaskCardWrapper";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import GroupTitle from "@coworker/apprestructured/src/TasksOverview/components/GroupTitle/GroupTitle";
import { getRefillAreaIdFromLocation } from "@fixa/platform-shared/dist/storeLocations/categoriseLocations";
import { TaskCardSkeleton } from "@coworker/apprestructured/src/tasks/components/TaskCard/TaskCardSkeleton";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { TasksTabs } from "../../constants/tasksTabs";
import { MultiSelectPanel } from "../MultiSelect/MultiSelectPanel";
import { mapFilterValuesToObject } from "../../helpers/mapFilterValuesToObject";
import { DEFAULT_FILTER_VALUES } from "../../constants/defaultFilterValues";
import { getAppliedFiltersCount } from "../../helpers/getAppliedFiltersCount";
import { useTranslation } from "@coworker/locales";
import { getGroupByLocation } from "../../helpers/getGroupByLocation";
import { TaskTabFilters } from "../../hooks/useTasksOverviewTabs";

type TaskOrHeaderListItem =
  | TaskListItem
  | { type: "group-title"; areaId: string };

interface TabPanelProps {
  tab: TasksTabs;
  filters: TaskTabFilters;
  onShowTaskDetails: (taskId: string, taskType: TaskType) => void;
}

export const TabPanel = ({
  tab,
  filters,
  onShowTaskDetails,
}: TabPanelProps) => {
  const { t } = useTranslation();

  const filtersValues = useMemo(() => {
    return {
      ...DEFAULT_FILTER_VALUES,
      ...mapFilterValuesToObject(filters.filterValues),
    };
  }, [filters.filterValues]);

  const tasksQueryResult = useTasksOverview(filtersValues, tab);

  const tasks = useMemo(() => {
    return tasksQueryResult?.data?.pages.map((page) => page.list).flat() || [];
  }, [tasksQueryResult?.data?.pages]);

  const filteredCount = tasksQueryResult?.data?.pages[0]?.filteredCount || 0;
  const totalCount = tasksQueryResult?.data?.pages[0]?.totalCount || 0;
  const isLoading = tasksQueryResult?.isLoading || false;
  const isFetching = tasksQueryResult?.isFetching || false;

  const itemNos = useMemo(() => {
    return tasks
      .filter(({ type }) => type === "v1")
      .map(({ task_data: task }) => (task as FullTask).product_article_id)
      .filter(Boolean);
  }, [tasks]);

  const itemsInfoResult = useItemsInfoQueries(itemNos);

  const itemsInfo = useMemo(() => {
    return itemsInfoResult.map((result) => result.data).filter(Boolean);
  }, [itemsInfoResult]);

  const uid = useUID();
  const { toggleModal } = React.useContext(ModalContext);
  const teamId = useTeamId();
  const [, setSearchParams] = useSearchParamState();

  const appliedFiltersCount = useMemo(() => {
    return getAppliedFiltersCount(filtersValues);
  }, [filtersValues]);

  const groupBy = filtersValues.groupBy ?? "";

  const handleTaskV2CardClick = useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", String(taskId));
      });
    },
    [setSearchParams]
  );

  const tasksAndHeaders: TaskOrHeaderListItem[] = useMemo(() => {
    if (!groupBy) {
      return tasks;
    }

    const result: TaskOrHeaderListItem[] = [];
    let prevAreaId: string | null = null;
    tasks.forEach((taskListItem: TaskListItem) => {
      const { type, task_data: task } = taskListItem;
      if (type === "v1") {
        const location = getGroupByLocation(task, groupBy);
        const areaId = getRefillAreaIdFromLocation(location, {
          enableSFM: true,
        });
        if (areaId !== prevAreaId) {
          result.push({ type: "group-title", areaId });
          prevAreaId = areaId;
        }
      }
      result.push(taskListItem);
    });
    return result;
  }, [groupBy, tasks]);

  const showLoadMore =
    tasks.length < filteredCount && !isLoading && !isFetching;
  const showNoResults = !isLoading && !isFetching && !tasks.length;

  return (
    <div className={styles["tabPanel"]}>
      <div className={styles["title"]}>
        {!isLoading && (
          <>
            {(tab === TasksTabs.OPEN || tab === TasksTabs.IN_PROGRESS) && (
              <FixaButton
                iconOnly
                ssrIcon={StackIconPath}
                size="xsmall"
                type="tertiary"
                onClick={() => {
                  trackerHelper.trackMultiSelectButtonTab(tab);
                  toggleModal({
                    type: ModalType.MultiSelectTasks,
                    payload: {
                      type: tab,
                    },
                  });
                }}
              />
            )}
            {filteredCount < totalCount && (
              <FixaText textStyle="Heading.XS">
                {`${filteredCount}/${totalCount} ${t("TasksString")}`}
              </FixaText>
            )}
            {filteredCount === totalCount && (
              <FixaText textStyle="Heading.XS">
                {`${totalCount} ${totalCount === 1 ? t("TaskString") : t("TasksString")}`}
              </FixaText>
            )}
          </>
        )}
      </div>
      <div className={styles["wrapper"]}>
        {!isLoading &&
          tasksAndHeaders.length > 0 &&
          tasksAndHeaders.map((taskOrHeaderListItem) => {
            const { type } = taskOrHeaderListItem;
            switch (type) {
              case "v1": {
                const taskListItem = taskOrHeaderListItem as TaskListItem & {
                  type: "v1";
                };
                const task = taskListItem.task_data;
                return (
                  <SwipeableTaskCard
                    key={`v1_${task.id}`}
                    task={task}
                    item={
                      itemsInfo?.find(
                        (item) => item?.no === task.product_article_id
                      ) as Item
                    }
                    multiSelect={false}
                    isMyTasks={task.assigned_user_id === uid}
                    type={tab}
                    disableSwipe={
                      task.state === "COMPLETED" || task.state === "CLOSED"
                    }
                    disablePickup={
                      !shouldDisplayPickupTask(task, {
                        uid: "",
                        team_id: teamId,
                        role_id: "",
                      })
                    }
                    onShowTaskDetails={onShowTaskDetails}
                  />
                );
              }
              case "v2": {
                const taskListItem = taskOrHeaderListItem as TaskListItem & {
                  type: "v2";
                };
                return (
                  <ListableTaskCardWrapper
                    key={`v2_${taskListItem.task_data.id}`}
                    onSelect={() =>
                      handleTaskV2CardClick(taskListItem.task_data.id)
                    }
                  >
                    <TaskCard task={taskListItem.task_data} />
                  </ListableTaskCardWrapper>
                );
              }
              case "group-title": {
                const groupItem = taskOrHeaderListItem as {
                  type: "group-title";
                  areaId: string;
                };
                return (
                  <GroupTitle
                    key={`area_${groupItem.areaId}`}
                    areaId={groupItem.areaId}
                  />
                );
              }
            }
          })}
        {showNoResults && (
          <div className={styles["noResults"]}>
            <FixaText textStyle="Heading.M">
              {t("noTaskResultsString")}
            </FixaText>
            {!tasks.length && appliedFiltersCount > 0 ? (
              <>
                <FixaText textStyle="Body.M">
                  {t("noTasksWithAppliedFiltersString")}
                </FixaText>
                <FixaButton
                  onClick={filters.resetFilters}
                  text={<Trans>clearFiltersString</Trans>}
                  size="small"
                  type="secondary"
                  className={styles["clearFiltersButton"]}
                />
              </>
            ) : (
              <FixaText textStyle="Body.M">
                {t("noTasksAvailableString")}
              </FixaText>
            )}
          </div>
        )}
        {(isLoading || isFetching) && (
          <div className={styles["skeleton"]}>
            <TaskCardSkeleton />
            <TaskCardSkeleton />
            <TaskCardSkeleton />
            <TaskCardSkeleton />
          </div>
        )}
        {showLoadMore && (
          <LoadMore
            onLoadMoreClick={tasksQueryResult?.fetchNextPage}
            loadedItemsCount={tasks.length}
            totalCount={filteredCount}
            isFetching={isFetching}
          />
        )}
      </div>
      <MultiSelectPanel
        filters={filtersValues}
        onShowTaskDetails={onShowTaskDetails}
        title={
          tab === TasksTabs.OPEN
            ? t("openTasksString")
            : tab === TasksTabs.IN_PROGRESS
              ? t("inProgressTasksString")
              : ""
        }
      />
    </div>
  );
};
